import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface DatasetRow {
  readonly datasetId: string;
  readonly datasetCode: string;
  readonly datasetName: string;
  readonly datasetType: string;
  readonly sourceType: string;
  readonly refreshFrequency: string;
  readonly rowCount: string;
  readonly status: string;
}

interface DataModelRow {
  readonly dataModelId: string;
  readonly modelCode: string;
  readonly modelName: string;
  readonly modelType: string;
  readonly status: string;
}

interface MetricRow {
  readonly metricId: string;
  readonly metricCode: string;
  readonly metricName: string;
  readonly expressionType: string;
  readonly dataType: string;
  readonly aggregation: string;
  readonly unit: string | null;
  readonly status: string;
}

const DATASET_TYPES = ['FACT', 'DIMENSION', 'AGGREGATE', 'TIME_SERIES', 'HIERARCHICAL', 'KPI_MODEL'];
const SOURCE_TYPES = ['M26_MART', 'SEMANTIC_LAYER', 'MODULE_TABLE', 'QUERY', 'VIEW'];
const REFRESH_FREQS = ['REALTIME', 'HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY', 'ON_DEMAND'];
const DATASET_STATUSES = ['DRAFT', 'ACTIVE', 'STALE', 'ARCHIVED'];
const MODEL_TYPES = ['STAR', 'SNOWFLAKE', 'FLAT', 'HIERARCHICAL', 'SEMANTIC'];
const EXPRESSION_TYPES = ['SIMPLE', 'DERIVED', 'CALCULATED', 'RATIO'];
const DATA_TYPES = ['INTEGER', 'DECIMAL', 'PERCENT', 'CURRENCY', 'DURATION', 'COUNT'];
const AGGREGATIONS = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT', 'RATIO', 'LAST', 'CUSTOM'];

/**
 * M37 — Data Explorer (§28 / §10-§11 Data Layer). The tenant's analytics data
 * fabric: analytics datasets (T496) that point at M26 marts / module tables
 * (reference only, no copy — BR-03 freshness), the data models (T498) that
 * shape facts and dimensions, and the metric definitions (T497) evaluated over
 * them. Authors create datasets / models / metrics (…_MANAGE) and refresh a
 * dataset's freshness (DATASET_MANAGE). Tenant isolation is enforced
 * server-side (LAW 4). A 403 surfaces a clear permission message.
 */
export function DataExplorerScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  // Datasets
  const [datasets, setDatasets] = useState<DatasetRow[]>([]);
  const [datasetTotal, setDatasetTotal] = useState(0);
  const [dsType, setDsType] = useState('');
  const [dsStatus, setDsStatus] = useState('');
  const [dsLoading, setDsLoading] = useState(true);
  const [dsName, setDsName] = useState('');
  const [dsNewType, setDsNewType] = useState('FACT');
  const [dsSourceType, setDsSourceType] = useState('M26_MART');
  const [dsSourceRef, setDsSourceRef] = useState('');
  const [dsRefresh, setDsRefresh] = useState('DAILY');
  const [dsBusy, setDsBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Data models
  const [models, setModels] = useState<DataModelRow[]>([]);
  const [modelTotal, setModelTotal] = useState(0);
  const [mdLoading, setMdLoading] = useState(true);
  const [mdName, setMdName] = useState('');
  const [mdType, setMdType] = useState('STAR');
  const [mdFactDataset, setMdFactDataset] = useState('');
  const [mdBusy, setMdBusy] = useState(false);

  // Metrics
  const [metrics, setMetrics] = useState<MetricRow[]>([]);
  const [metricTotal, setMetricTotal] = useState(0);
  const [mtLoading, setMtLoading] = useState(true);
  const [mtName, setMtName] = useState('');
  const [mtFormula, setMtFormula] = useState('');
  const [mtExpr, setMtExpr] = useState('SIMPLE');
  const [mtDataType, setMtDataType] = useState('DECIMAL');
  const [mtAgg, setMtAgg] = useState('SUM');
  const [mtUnit, setMtUnit] = useState('');
  const [mtBusy, setMtBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setDsLoading(true);
    const params = new URLSearchParams();
    if (dsType) params.set('datasetType', dsType);
    if (dsStatus) params.set('status', dsStatus);
    const qs = params.toString();
    apiRequest<{ data: DatasetRow[]; total: number }>(`/v1/bi/datasets${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setDatasets(r.data); setDatasetTotal(r.total); } })
      .catch((e) => { if (alive) setActionError((e as Error).message); })
      .finally(() => { if (alive) setDsLoading(false); });
    return () => { alive = false; };
  }, [token, dsType, dsStatus, reload]);

  useEffect(() => {
    let alive = true;
    setMdLoading(true);
    apiRequest<{ data: DataModelRow[]; total: number }>(`/v1/bi/data-models`, { token })
      .then((r) => { if (alive) { setModels(r.data); setModelTotal(r.total); } })
      .catch((e) => { if (alive) setActionError((e as Error).message); })
      .finally(() => { if (alive) setMdLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  useEffect(() => {
    let alive = true;
    setMtLoading(true);
    apiRequest<{ data: MetricRow[]; total: number }>(`/v1/bi/metrics`, { token })
      .then((r) => { if (alive) { setMetrics(r.data); setMetricTotal(r.total); } })
      .catch((e) => { if (alive) setActionError((e as Error).message); })
      .finally(() => { if (alive) setMtLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  const handle403 = (err: unknown, perm: string): void => {
    if (err instanceof ApiError && err.status === 403) {
      setActionError(t('m37.explorer.notAuth', { perm }));
    } else {
      setActionError((err as Error).message);
    }
  };

  const createDataset = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null); setNotice(null);
    if (!dsName.trim()) { setActionError(t('m37.explorer.datasetNameRequired')); return; }
    setDsBusy(true);
    try {
      const r = await apiRequest<{ data: DatasetRow }>(`/v1/bi/datasets`, {
        method: 'POST',
        body: { datasetName: dsName.trim(), datasetType: dsNewType, sourceType: dsSourceType, sourceRef: dsSourceRef.trim() || undefined, refreshFrequency: dsRefresh },
        token,
      });
      setNotice(t('m37.explorer.datasetCreated', { code: r.data.datasetCode }));
      setDsName(''); setDsSourceRef(''); setReload((n) => n + 1);
    } catch (err) { handle403(err, 'DATASET_MANAGE'); } finally { setDsBusy(false); }
  };

  const refreshDataset = async (id: string): Promise<void> => {
    setActionError(null); setNotice(null); setBusyId(id);
    try {
      const r = await apiRequest<{ data: DatasetRow }>(`/v1/bi/datasets/${id}/refresh`, { method: 'POST', body: {}, token });
      setNotice(t('m37.explorer.datasetRefreshed', { code: r.data.datasetCode, status: r.data.status }));
      setReload((n) => n + 1);
    } catch (err) { handle403(err, 'DATASET_MANAGE'); } finally { setBusyId(null); }
  };

  const createModel = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null); setNotice(null);
    if (!mdName.trim()) { setActionError(t('m37.explorer.modelNameRequired')); return; }
    setMdBusy(true);
    try {
      const r = await apiRequest<{ data: DataModelRow }>(`/v1/bi/data-models`, {
        method: 'POST',
        body: { modelName: mdName.trim(), modelType: mdType, factDatasetId: mdFactDataset.trim() || undefined },
        token,
      });
      setNotice(t('m37.explorer.modelCreated', { code: r.data.modelCode }));
      setMdName(''); setMdFactDataset(''); setReload((n) => n + 1);
    } catch (err) { handle403(err, 'DATAMODEL_MANAGE'); } finally { setMdBusy(false); }
  };

  const createMetric = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null); setNotice(null);
    if (!mtName.trim()) { setActionError(t('m37.explorer.metricNameRequired')); return; }
    if (!mtFormula.trim()) { setActionError(t('m37.explorer.metricFormulaRequired')); return; }
    setMtBusy(true);
    try {
      const r = await apiRequest<{ data: MetricRow }>(`/v1/bi/metrics`, {
        method: 'POST',
        body: { metricName: mtName.trim(), formula: mtFormula.trim(), expressionType: mtExpr, dataType: mtDataType, aggregation: mtAgg, unit: mtUnit.trim() || undefined },
        token,
      });
      setNotice(t('m37.explorer.metricCreated', { code: r.data.metricCode }));
      setMtName(''); setMtFormula(''); setMtUnit(''); setReload((n) => n + 1);
    } catch (err) { handle403(err, 'METRIC_MANAGE'); } finally { setMtBusy(false); }
  };

  return (
    <main aria-label="data-explorer">
      <h1>{t('m37.explorer.title')}</h1>
      {actionError && <p role="alert" aria-label="action-error">{actionError}</p>}
      {notice && <p role="status" aria-label="notice">{notice}</p>}

      <section aria-label="datasets">
        <h2>{t('m37.explorer.datasetsTitle')}</h2>
        <form aria-label="dataset-form" onSubmit={(e) => void createDataset(e)}>
          <label>{t('m37.common.name')}<input aria-label="ds-name" value={dsName} onChange={(e) => setDsName(e.target.value)} /></label>
          <label>{t('m37.common.type')}
            <select aria-label="ds-type" value={dsNewType} onChange={(e) => setDsNewType(e.target.value)}>
              {DATASET_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.explorer.sourceType')}
            <select aria-label="ds-source-type" value={dsSourceType} onChange={(e) => setDsSourceType(e.target.value)}>
              {SOURCE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.explorer.sourceRef')}<input aria-label="ds-source-ref" value={dsSourceRef} onChange={(e) => setDsSourceRef(e.target.value)} placeholder={t('m37.explorer.sourceRefPlaceholder')} /></label>
          <label>{t('m37.explorer.refresh')}
            <select aria-label="ds-refresh" value={dsRefresh} onChange={(e) => setDsRefresh(e.target.value)}>
              {REFRESH_FREQS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-dataset" disabled={dsBusy}>{dsBusy ? t('m37.common.saving') : t('m37.explorer.createDataset')}</Button>
        </form>
        <div className="filters">
          <label>{t('m37.common.type')}
            <select aria-label="filter-ds-type" value={dsType} onChange={(e) => setDsType(e.target.value)}>
              {['', ...DATASET_TYPES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.status')}
            <select aria-label="filter-ds-status" value={dsStatus} onChange={(e) => setDsStatus(e.target.value)}>
              {['', ...DATASET_STATUSES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
        </div>
        {dsLoading ? <p>{t('common.loading')}</p> : (
          <>
            <p aria-label="dataset-count">{t('m37.explorer.datasetCount', { count: datasetTotal })}</p>
            <DataTable
              ariaLabel="dataset-table"
              testId="dataset-table"
              rows={datasets}
              rowKey={(r) => r.datasetId}
              columns={[
                { key: 'datasetCode', header: t('m37.common.code'), render: (r) => r.datasetCode },
                { key: 'datasetName', header: t('m37.common.name'), render: (r) => r.datasetName },
                { key: 'datasetType', header: t('m37.common.type'), render: (r) => r.datasetType },
                { key: 'sourceType', header: t('m37.explorer.colSource'), render: (r) => r.sourceType },
                { key: 'refreshFrequency', header: t('m37.explorer.refresh'), render: (r) => r.refreshFrequency },
                { key: 'rowCount', header: t('m37.explorer.colRows'), render: (r) => r.rowCount },
                { key: 'status', header: t('m37.common.status'), render: (r) => r.status },
                { key: 'actions', header: t('m37.common.actions'), render: (r) => <Button variant="secondary" type="button" aria-label={`refresh-${r.datasetId}`} disabled={busyId === r.datasetId} onClick={() => void refreshDataset(r.datasetId)}>{t('m37.explorer.refresh')}</Button> },
              ]}
            />
          </>
        )}
      </section>

      <section aria-label="data-models">
        <h2>{t('m37.explorer.modelsTitle')}</h2>
        <form aria-label="model-form" onSubmit={(e) => void createModel(e)}>
          <label>{t('m37.common.name')}<input aria-label="md-name" value={mdName} onChange={(e) => setMdName(e.target.value)} /></label>
          <label>{t('m37.common.type')}
            <select aria-label="md-type" value={mdType} onChange={(e) => setMdType(e.target.value)}>
              {MODEL_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.explorer.factDatasetId')}<input aria-label="md-fact" value={mdFactDataset} onChange={(e) => setMdFactDataset(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-model" disabled={mdBusy}>{mdBusy ? t('m37.common.saving') : t('m37.explorer.createModel')}</Button>
        </form>
        {mdLoading ? <p>{t('common.loading')}</p> : (
          <>
            <p aria-label="model-count">{t('m37.explorer.modelCount', { count: modelTotal })}</p>
            <DataTable
              ariaLabel="model-table"
              testId="model-table"
              rows={models}
              rowKey={(r) => r.dataModelId}
              columns={[
                { key: 'modelCode', header: t('m37.common.code'), render: (r) => r.modelCode },
                { key: 'modelName', header: t('m37.common.name'), render: (r) => r.modelName },
                { key: 'modelType', header: t('m37.common.type'), render: (r) => r.modelType },
                { key: 'status', header: t('m37.common.status'), render: (r) => r.status },
              ]}
            />
          </>
        )}
      </section>

      <section aria-label="metrics">
        <h2>{t('m37.explorer.metricsTitle')}</h2>
        <form aria-label="metric-form" onSubmit={(e) => void createMetric(e)}>
          <label>{t('m37.common.name')}<input aria-label="mt-name" value={mtName} onChange={(e) => setMtName(e.target.value)} /></label>
          <label>{t('m37.common.formula')}<input aria-label="mt-formula" value={mtFormula} onChange={(e) => setMtFormula(e.target.value)} /></label>
          <label>{t('m37.explorer.expression')}
            <select aria-label="mt-expr" value={mtExpr} onChange={(e) => setMtExpr(e.target.value)}>
              {EXPRESSION_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.explorer.dataType')}
            <select aria-label="mt-data-type" value={mtDataType} onChange={(e) => setMtDataType(e.target.value)}>
              {DATA_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.aggregation')}
            <select aria-label="mt-agg" value={mtAgg} onChange={(e) => setMtAgg(e.target.value)}>
              {AGGREGATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.unit')}<input aria-label="mt-unit" value={mtUnit} onChange={(e) => setMtUnit(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-metric" disabled={mtBusy}>{mtBusy ? t('m37.common.saving') : t('m37.explorer.createMetric')}</Button>
        </form>
        {mtLoading ? <p>{t('common.loading')}</p> : (
          <>
            <p aria-label="metric-count">{t('m37.explorer.metricCount', { count: metricTotal })}</p>
            <DataTable
              ariaLabel="metric-table"
              testId="metric-table"
              rows={metrics}
              rowKey={(r) => r.metricId}
              columns={[
                { key: 'metricCode', header: t('m37.common.code'), render: (r) => r.metricCode },
                { key: 'metricName', header: t('m37.common.name'), render: (r) => r.metricName },
                { key: 'expressionType', header: t('m37.explorer.expression'), render: (r) => r.expressionType },
                { key: 'dataType', header: t('m37.explorer.dataType'), render: (r) => r.dataType },
                { key: 'aggregation', header: t('m37.common.aggregation'), render: (r) => r.aggregation },
                { key: 'unit', header: t('m37.common.unit'), render: (r) => r.unit ?? '—' },
                { key: 'status', header: t('m37.common.status'), render: (r) => r.status },
              ]}
            />
          </>
        )}
      </section>
    </main>
  );
}
